import type React from 'react'
import { Separator } from '@components/ui/separator'
import { cn } from '@/utils'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { EffectsPanelInset } from './components/effects-panel-inset'
import { EffectsPanelRounding } from './components/effects-panel-rounding'
import { EffectsPanelSides } from './components/effects-panel-sides'
import { EffectsPanelTransparency } from './components/effects-panel-transparency'
import { GradientControls } from './components/gradient-controls'
import { ShadowControls } from './components/shadow-controls'

interface EffectsPanelProps {
  className?: string
  /** When true, all controls are non-interactive (e.g. selection includes a locked object). */
  disabled?: boolean
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  onShapeCommitted?: (
    shape: ShapeData,
    source?: 'move' | 'resize' | 'rotate' | 'update',
    undo?: ShapeCommittedUndoMeta
  ) => void
  onBatchShapesCommitted?: (
    items: BatchShapeTransformItem[],
    source: 'rotate' | 'move' | 'align' | 'resize' | 'update',
    meta?: { historyTxnId?: string }
  ) => void
  /** Transparency: clear selection after committed value (object effects spec). */
  onTransparencyCommitApplied?: () => void
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground leading-normal">{children}</label>
    </div>
  )
}

export const EffectsPanel: React.FC<EffectsPanelProps> = ({
  className,
  disabled = false,
  shapeManagerRef,
  onShapeCommitted,
  onBatchShapesCommitted,
  onTransparencyCommitApplied,
}) => {
  const controlProps = { disabled, shapeManagerRef, onShapeCommitted, onBatchShapesCommitted }

  return (
    <div className={cn('space-y-4 pb-4', className)} aria-disabled={disabled || undefined}>
      <SectionHeading>Geometry</SectionHeading>
      <EffectsPanelRounding {...controlProps} />
      <EffectsPanelSides {...controlProps} />
      <EffectsPanelInset {...controlProps} />

      <Separator />

      <SectionHeading>Fill</SectionHeading>
      <EffectsPanelTransparency
        {...controlProps}
        onTransparencyCommitApplied={onTransparencyCommitApplied}
      />

      <div className={cn('space-y-4', disabled && 'pointer-events-none select-none opacity-60')}>
        <GradientControls disabled={disabled} />

        <Separator />

        <SectionHeading>Depth</SectionHeading>
        <ShadowControls disabled={disabled} />
      </div>
    </div>
  )
}
